import { z } from "zod";
import { TASK_STATUSES } from "./tasks";

/**
 * Org-configurable "Task Pages" — task-queue pages mounted into the sidebar.
 *
 * A task page pins the tasks queue to one or more task types (one tab each;
 * see the taskPages contract's `taskTypes`) and (optionally) attaches
 * itself to an existing sidebar section. Pages are data (core_task_pages), not
 * routes, so the allowed section labels are centralized here for both the
 * taskPages contract enum and the admin UI picker.
 */

/**
 * Default filters seeding a task page's queue view — a subset of the tasks
 * queue URL search params. The URL still drives the live view; these only
 * seed it on first visit. Shared by the taskPages contract (validation) and
 * the backend TaskPage model (JSONB column typing).
 */
/**
 * `user:<uuid>` / `agent:<uuid>` — the queue URL's assignee presets (see the
 * frontend's taskQueueSearch.ts, which slices on these prefixes). `unassigned`
 * is folded into this pattern, not a third union member: a new `anyOf` branch
 * on the task-page response is an OpenAPI breaking change.
 */
const ASSIGNEE_PRESET_PATTERN =
  /^(?:unassigned|(?:user|agent):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export const TaskPageDefaultFiltersSchema = z.object({
  status: z.array(z.enum(TASK_STATUSES)).optional(),
  /** 'mine' | 'unassigned' | 'user:<uuid>' | 'agent:<uuid>' — queue URL presets. */
  assignee: z
    .union([z.literal("mine"), z.string().regex(ASSIGNEE_PRESET_PATTERN)])
    .optional(),
  sortBy: z
    .enum(["createdAt", "updatedAt", "dueDate", "priority", "status", "title"])
    .optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
});

export type TaskPageDefaultFilters = z.infer<
  typeof TaskPageDefaultFiltersSchema
>;

/**
 * Sidebar group labels a task page may attach to.
 *
 * IMPORTANT: these must stay in sync with the `staticData.app.nav.group`
 * labels declared on routes (frontend `src/routes.tsx` and co-located route
 * modules under `src/pages`). They are plain string literals there — renaming
 * a sidebar group requires updating this list too, or pages assigned to the
 * old label fall back to the generic "Pages" section.
 */
export const TASK_PAGE_NAV_GROUPS = [
  "Overview",
  "Assistant",
  "Sales",
  "Organization",
  "Inventory Management",
  "Item Master",
  "Procurement",
  "Pricing",
  "Accounts Payable",
  "Accounts Receivable",
  "Rebates",
  "Ship and Debit",
  "Ship and Debit Admin",
  "Admin",
] as const;

export type TaskPageNavGroup = (typeof TASK_PAGE_NAV_GROUPS)[number];

/**
 * Small fixed icon vocabulary for task pages. Each key maps to one Phosphor
 * icon in the frontend (`taskPageIcons.tsx`); unknown/null keys render the
 * default list icon.
 */
export const TASK_PAGE_ICON_KEYS = [
  "list-checks",
  "envelope",
  "invoice",
  "tray",
  "clipboard",
  "files",
  "truck",
  "currency-dollar",
  "users",
  "robot",
] as const;

export type TaskPageIconKey = (typeof TASK_PAGE_ICON_KEYS)[number];
